/**
 * =============================================================================
 * TemplateImportDialog — envio, conferência e revisão antes de aplicar
 *
 * Três momentos em uma única janela:
 *   1. Envio do arquivo `.uaizytemplate`
 *   2. Conferência automática (nada é alterado nesta etapa)
 *   3. Revisão: o que o pacote traz, escolha item a item, responsável padrão
 *      e confirmação da aplicação
 *
 * @version 1.2.0
 * RELEASE: 2026-09-08 v1.2.0 - Cabeçalho alinhado ao padrão editorial dos
 *          modais grandes (bg-sidebar, título único, X branco, sem subtítulo).
 * RELEASE: 2026-09-08 v1.1.0 - Janela ampla (95vw x 97vh) para acompanhar o
 *          novo seletor em mestre x detalhe.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (refatoração da tela).
 * =============================================================================
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { TemplateModuleSelector } from './TemplateModuleSelector';
import {
  useApproveTemplatePlan,
  useSetTemplatePlanOwner,
  useTemplateJob,
  useTemplatePackageItems,
  useTemplatePlan,
  useTemplateTenantMembers,
  useUploadTemplatePackage,
} from '@/hooks/useSystemTemplates';
import { toast } from '@/lib/toast';
import { TEMPLATE_MODULE_CATALOG } from '@/lib/template-system/moduleCatalog';
import {
  TEMPLATE_PACKAGE_EXTENSION,
  type TemplateModuleKey,
} from '@/lib/template-system/constants';
import type { TemplateSelection } from '@/lib/template-system/api';

interface TemplateImportDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  tenantId: string | null;
  userId: string | null;
  /** Versão já conferida, quando a janela é aberta direto na revisão. */
  versionId?: string | null;
  /** Avisa o id da execução criada/aprovada, para acompanhar na tela. */
  onJobStarted: (jobId: string) => void;
}

export function TemplateImportDialog({
  open,
  onOpenChange,
  tenantId,
  userId,
  versionId = null,
  onJobStarted,
}: TemplateImportDialogProps) {
  const inputArquivoRef = useRef<HTMLInputElement>(null);

  // Execução de conferência iniciada dentro desta janela
  const [jobConferencia, setJobConferencia] = useState<string | null>(null);
  // Versão em revisão (vem de fora ou da conferência recém-concluída)
  const [versaoEmRevisao, setVersaoEmRevisao] = useState<string | null>(versionId);
  const [responsavel, setResponsavel] = useState('');
  const [modulos, setModulos] = useState<string[]>([]);
  const [selecao, setSelecao] = useState<TemplateSelection>({});

  const enviarMutation = useUploadTemplatePackage();
  const aprovarMutation = useApproveTemplatePlan();
  const responsavelMutation = useSetTemplatePlanOwner();

  const { data: job } = useTemplateJob(jobConferencia);
  const { data: plano, isLoading: carregandoPlano } = useTemplatePlan(versaoEmRevisao);
  const { data: itensDoPacote = {}, isLoading: carregandoItens } =
    useTemplatePackageItems(versaoEmRevisao);
  const { data: pessoas = [] } = useTemplateTenantMembers(versaoEmRevisao ? tenantId : null);

  // Quando a conferência termina, abre a revisão automaticamente
  useEffect(() => {
    if (job?.kind === 'import' && job.status === 'completed' && job.package_version_id) {
      setVersaoEmRevisao(job.package_version_id);
    }
  }, [job]);

  // Sincroniza a versão informada por fora
  useEffect(() => {
    if (open) setVersaoEmRevisao(versionId);
  }, [open, versionId]);

  // Sugere o próprio usuário como responsável padrão
  useEffect(() => {
    if (!versaoEmRevisao) return;
    if (!responsavel && userId && pessoas.some((p) => p.user_id === userId)) {
      setResponsavel(userId);
    }
  }, [versaoEmRevisao, pessoas, responsavel, userId]);

  // Por padrão, tudo o que o pacote traz vem marcado
  useEffect(() => {
    const chaves = Object.keys(itensDoPacote);
    if (chaves.length > 0) setModulos((atual) => (atual.length === 0 ? chaves : atual));
  }, [itensDoPacote]);

  // Limpa o estado ao fechar
  useEffect(() => {
    if (!open) {
      setJobConferencia(null);
      setVersaoEmRevisao(null);
      setResponsavel('');
      setModulos([]);
      setSelecao({});
    }
  }, [open]);

  /** Módulos que o pacote traz, com nome amigável. */
  const modulosDoPacote = useMemo(
    () =>
      Object.keys(itensDoPacote).map((key) => ({
        key,
        name: TEMPLATE_MODULE_CATALOG[key as TemplateModuleKey]?.name ?? key,
        description:
          TEMPLATE_MODULE_CATALOG[key as TemplateModuleKey]?.description ??
          'Conteúdo trazido pelo arquivo.',
      })),
    [itensDoPacote],
  );

  /** Envia o arquivo escolhido e inicia a conferência. */
  const enviarArquivo = async (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!arquivo || !tenantId) return;

    try {
      const resultado = await enviarMutation.mutateAsync({ tenantId, file: arquivo });
      setJobConferencia(resultado.job_id);
      onJobStarted(resultado.job_id);
      toast.success('Arquivo recebido. A conferência começou — nada foi alterado ainda.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível enviar o arquivo.');
    }
  };

  /** Grava responsável e seleção e libera a aplicação. */
  const aplicar = async () => {
    if (!plano?.id) return;
    if (!responsavel) {
      toast.error('Escolha quem ficará como responsável pelos cadastros criados.');
      return;
    }
    if (modulos.length === 0) {
      toast.error('Escolha ao menos um módulo para aplicar.');
      return;
    }

    // Só aplica o que estiver marcado: módulos fora da lista ficam de fora
    const selecaoFinal: TemplateSelection = {};
    for (const chave of modulos) {
      // Sem detalhamento: leva o módulo inteiro (null = tudo)
      selecaoFinal[chave] = selecao[chave] ?? (null as never);
    }

    try {
      await responsavelMutation.mutateAsync({
        planId: plano.id,
        ownerUserId: responsavel,
        selection: selecaoFinal,
      });
      const novoJob = await aprovarMutation.mutateAsync(plano.id);
      onJobStarted(novoJob);
      onOpenChange(false);
      toast.success('Aplicação iniciada. Acompanhe o andamento na lista.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível aplicar o template.');
    }
  };

  const emRevisao = !!versaoEmRevisao;
  const salvando = aprovarMutation.isPending || responsavelMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-[95vw] w-[95vw] h-[97vh] p-0 gap-0 overflow-hidden flex flex-col"
      >
        {/* ─────────── Cabeçalho editorial (faixa escura de alto contraste) ─────────── */}
        <header className="shrink-0 bg-sidebar px-5 py-4 text-sidebar-foreground sm:px-7">
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {emRevisao ? 'Revisar antes de aplicar' : 'Importar template'}
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm text-sidebar-foreground/70 opacity-80 transition-opacity hover:text-sidebar-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-sidebar-foreground/40">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </header>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* Etapa 1 e 2: envio e conferência */}
          {!emRevisao && (
            <>
              <input
                ref={inputArquivoRef}
                type="file"
                accept={`${TEMPLATE_PACKAGE_EXTENSION},application/json`}
                className="hidden"
                onChange={enviarArquivo}
              />

              <Card className="flex flex-col items-center gap-3 border-dashed p-8 text-center">
                <Upload className="h-7 w-7 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Escolha o arquivo do template (até 50 MB).
                </p>
                <Button
                  onClick={() => inputArquivoRef.current?.click()}
                  disabled={enviarMutation.isPending || !tenantId}
                >
                  {enviarMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Escolher arquivo
                </Button>
              </Card>

              {job && (
                <div className="space-y-2">
                  <p className="text-sm">
                    Conferindo o arquivo… nada foi alterado nesta conta ainda.
                  </p>
                  <Progress value={job.progress_percent} />
                  {job.status === 'failed' && (
                    <InlineNotice variant="error">
                      {(job.error_payload?.message as string) ?? 'A conferência falhou.'}
                    </InlineNotice>
                  )}
                </div>
              )}
            </>
          )}

          {/* Etapa 3: revisão */}
          {emRevisao && (
            <>
              {carregandoPlano && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando a prévia…
                </p>
              )}

              {!carregandoPlano && !plano && (
                <InlineNotice variant="warning">
                  A prévia ainda não está pronta. Aguarde alguns instantes e abra de novo.
                </InlineNotice>
              )}

              {plano && (
                <>
                  <InlineNotice variant="warning">
                    Esta prévia vale até {new Date(plano.expires_at).toLocaleString('pt-BR')}. Depois
                    disso será preciso enviar o arquivo de novo.
                  </InlineNotice>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Card className="p-3">
                      <p className="text-xs text-muted-foreground">Itens</p>
                      <p className="text-lg font-semibold">{plano.summary?.total_items ?? 0}</p>
                    </Card>
                    <Card className="p-3">
                      <p className="text-xs text-muted-foreground">Conflitos</p>
                      <p className="text-lg font-semibold">{plano.summary?.total_conflicts ?? 0}</p>
                    </Card>
                    <Card className="p-3">
                      <p className="text-xs text-muted-foreground">Sem correspondência</p>
                      <p className="text-lg font-semibold">
                        {plano.summary?.unresolved_references ?? 0}
                      </p>
                    </Card>
                  </div>

                  <div className="space-y-2 h-[55vh] flex flex-col">
                    <Label>O que será criado nesta conta</Label>
                    {carregandoItens ? (
                      <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Lendo o conteúdo do arquivo…
                      </p>
                    ) : modulosDoPacote.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Não foi possível detalhar o conteúdo. Tudo o que o arquivo traz será
                        aplicado.
                      </p>
                    ) : (
                      <div className="min-h-0 flex-1">
                      <TemplateModuleSelector
                        modules={modulosDoPacote}
                        selectedModules={modulos}
                        onToggleModule={(key) =>
                          setModulos((atual) =>
                            atual.includes(key)
                              ? atual.filter((m) => m !== key)
                              : [...atual, key],
                          )
                        }
                        selection={selecao}
                        onSelectionChange={setSelecao}
                        itemsByModule={itensDoPacote}
                      />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label>Responsável pelos cadastros criados</Label>
                    <Select value={responsavel} onValueChange={setResponsavel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Escolha uma pessoa desta conta" />
                      </SelectTrigger>
                      <SelectContent>
                        {pessoas.map((pessoa) => (
                          <SelectItem key={pessoa.user_id} value={pessoa.user_id}>
                            {pessoa.full_name || pessoa.email || 'Sem nome'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Usado em cadastros que precisam de um dono nesta conta, como agendas.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {emRevisao ? 'Cancelar' : 'Fechar'}
          </Button>
          {emRevisao && (
            <Button onClick={aplicar} disabled={!plano || !responsavel || salvando}>
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              Aplicar nesta conta
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateImportDialog;
